import { useMemo, useCallback } from 'react';
import { useSearch, useNavigate, useParams } from '@tanstack/react-router';
import { Card } from '@lean-agility/design-system/react';
import { ResultsSummary } from '@/components/molecules/ResultsSummary';
import { FeatureResultCard } from '@/components/molecules/FeatureResultCard';
import {
  FilterControls,
  NO_FEATURE_KEY,
  STATUS_FILTER_VALUES,
  type StatusFilterValue,
} from '@/components/molecules/FilterControls';
import { ViewModeToggle, type ViewMode } from '@/components/molecules/ViewModeToggle';
import { FeatureTimeline } from './FeatureTimeline';
import { processDeliverableResults, buildDependencyMap, buildDeliverableLookup } from '@/lib/utils';
import type { OrganizationMonteCarloResponse, ProcessedDeliverableResult, TeamResponse } from '@/types/api';
import type { DeliverableTimelineData } from '@/types/timeline';

export interface ResultsViewProps {
  result: OrganizationMonteCarloResponse;
  teamColors: Record<string, string>;
  /** Feature names in configuration order (from simulation setup) */
  featureOrder: string[];
  /** Feature due dates (feature name → due date) */
  featureDueDates: Map<string, Date | null>;
  /** Teams from simulation configuration (for building dependency map) */
  teams?: TeamResponse[];
  /** Effective simulation start date (resolved; null start_date → today). Anchors due-before-start warnings. */
  simulationStartDate: Date;
}

const FALLBACK_COLOR = '#636df1';

export function ResultsView({
  result,
  teamColors,
  featureOrder,
  featureDueDates,
  teams,
  simulationStartDate,
}: ResultsViewProps) {
  // Get simulation ID for session storage key
  const { simulationId } = useParams({ from: '/simulation/$simulationId/results' });

  // Read search params from URL
  const {
    view,
    groupMode,
    teams: urlTeams,
    features: urlFeatures,
    status: urlStatus,
  } = useSearch({
    from: '/simulation/$simulationId/results',
  });
  const navigate = useNavigate();

  const viewMode: ViewMode = view;
  const processedResults = processDeliverableResults(result.deliverables);

  // Build dependency map and lookup from teams configuration
  const dependencyMap = useMemo(() => (teams ? buildDependencyMap(teams) : undefined), [teams]);
  const deliverableLookup = useMemo(() => (teams ? buildDeliverableLookup(teams) : undefined), [teams]);

  // Extract unique teams and features from results
  // Features follow configuration order (featureOrder prop), not order of appearance
  const { uniqueTeams, uniqueFeatures } = useMemo(() => {
    const teamSet = new Set<string>();
    const featuresInResults = new Set<string>();
    let hasNoFeature = false;

    for (const d of processedResults) {
      teamSet.add(d.team_name);
      if (d.feature_name) {
        featuresInResults.add(d.feature_name);
      } else {
        hasNoFeature = true;
      }
    }

    // Use configuration order, filtered to only features present in results
    const features = featureOrder.filter((name) => featuresInResults.has(name));
    if (hasNoFeature) {
      features.push(NO_FEATURE_KEY);
    }

    return {
      uniqueTeams: Array.from(teamSet),
      uniqueFeatures: features,
    };
  }, [processedResults, featureOrder]);

  // Compute effective filters from URL params
  // undefined = not specified in URL, default to all selected (clean URL)
  // empty array = explicitly none selected
  // non-empty array = those specific items selected
  const teamFilters = useMemo(() => {
    if (urlTeams === undefined) {
      return new Set(uniqueTeams); // All selected by default
    }
    return new Set(urlTeams); // Could be empty or specific items
  }, [urlTeams, uniqueTeams]);

  const featureFilters = useMemo(() => {
    if (urlFeatures === undefined) {
      return new Set(uniqueFeatures); // All selected by default
    }
    return new Set(urlFeatures); // Could be empty or specific items
  }, [urlFeatures, uniqueFeatures]);

  const statusFilters = useMemo(() => {
    if (urlStatus === undefined) {
      return new Set(STATUS_FILTER_VALUES); // All selected by default
    }
    return new Set(urlStatus as StatusFilterValue[]); // Could be empty or specific items
  }, [urlStatus]);

  // Update URL when filters change
  const onTeamFiltersChange = useCallback(
    (newFilters: Set<string>) => {
      const teamsArray = Array.from(newFilters);
      // Only include in URL if not "all selected"
      const teams = teamsArray.length === uniqueTeams.length ? undefined : teamsArray;
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, teams }),
        replace: true,
      });
    },
    [navigate, uniqueTeams.length],
  );

  const onFeatureFiltersChange = useCallback(
    (newFilters: Set<string>) => {
      const featuresArray = Array.from(newFilters);
      // Only include in URL if not "all selected"
      const features = featuresArray.length === uniqueFeatures.length ? undefined : featuresArray;
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, features }),
        replace: true,
      });
    },
    [navigate, uniqueFeatures.length],
  );

  const onStatusFiltersChange = useCallback(
    (newFilters: Set<StatusFilterValue>) => {
      const statusArray = Array.from(newFilters);
      // Only include in URL if not "all selected"
      const status = statusArray.length === STATUS_FILTER_VALUES.length ? undefined : statusArray;
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, status }),
        replace: true,
      });
    },
    [navigate],
  );

  const setViewMode = useCallback(
    (newView: ViewMode) => {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, view: newView }),
        replace: true,
      });
    },
    [navigate],
  );

  const setGroupMode = useCallback(
    (newGroupMode: 'team' | 'feature') => {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, groupMode: newGroupMode }),
        replace: true,
      });
    },
    [navigate],
  );

  // Handle deliverable label click in timeline → navigate to config page for editing
  const handleDeliverableClick = useCallback(
    (data: DeliverableTimelineData) => {
      if (!data.deliverableId) return;
      navigate({
        to: '/simulation/$simulationId',
        params: { simulationId },
        search: (prev) => ({ ...prev, focus: data.deliverableId! }),
      });
    },
    [navigate, simulationId],
  );

  // Filter results based on team, feature, and status filters
  const filteredResults = useMemo(() => {
    // If no teams or features selected, show nothing
    if (teamFilters.size === 0 || featureFilters.size === 0 || statusFilters.size === 0) {
      return [];
    }

    let filtered = processedResults;

    // Filter by teams if not all selected
    if (teamFilters.size < uniqueTeams.length) {
      filtered = filtered.filter((d) => teamFilters.has(d.team_name));
    }

    // Filter by features if not all selected
    if (featureFilters.size < uniqueFeatures.length) {
      filtered = filtered.filter((d) => {
        const featureName = d.feature_name;
        if (!featureName) {
          return featureFilters.has(NO_FEATURE_KEY);
        }
        return featureFilters.has(featureName);
      });
    }

    // Filter by status if not all selected
    if (statusFilters.size < STATUS_FILTER_VALUES.length) {
      filtered = filtered.filter((d) => {
        const hasDueDate = !!d.due_date;

        // Check due date presence filters
        const passesDueDateFilter =
          (statusFilters.has('due') && hasDueDate) || (statusFilters.has('nodue') && !hasDueDate);

        // Check risk status filters (only apply to items with due dates)
        // Items without due dates pass through if 'nodue' is selected
        if (!hasDueDate) {
          return passesDueDateFilter;
        }

        // For items with due dates, also check risk filters
        const passesRiskFilter = (statusFilters.has('risk') && d.atRisk) || (statusFilters.has('safe') && !d.atRisk);

        // Item passes if it matches due date filter AND risk filter
        // But if only risk/safe selected (not due), still show matching items
        const dueDateFilterActive = statusFilters.has('due') || statusFilters.has('nodue');
        const riskFilterActive = statusFilters.has('risk') || statusFilters.has('safe');

        if (dueDateFilterActive && riskFilterActive) {
          return passesDueDateFilter && passesRiskFilter;
        } else if (dueDateFilterActive) {
          return passesDueDateFilter;
        } else if (riskFilterActive) {
          return passesRiskFilter;
        }

        return true;
      });
    }

    return filtered;
  }, [processedResults, teamFilters, featureFilters, statusFilters, uniqueTeams.length, uniqueFeatures.length]);

  // Group filtered results by team for cards view
  const filteredTeams = useMemo(() => {
    const teamMap = new Map<string, ProcessedDeliverableResult[]>();
    for (const deliverable of filteredResults) {
      const existing = teamMap.get(deliverable.team_name) || [];
      existing.push(deliverable);
      teamMap.set(deliverable.team_name, existing);
    }
    return Array.from(teamMap.entries());
  }, [filteredResults]);

  return (
    <div className="space-y-8">
      {/* Summary */}
      <ResultsSummary results={processedResults} totalSimulations={result.total_simulations} />

      {/* Shared Filter Controls with View Toggle */}
      <FilterControls
        uniqueTeams={uniqueTeams}
        uniqueFeatures={uniqueFeatures}
        teamFilters={teamFilters}
        featureFilters={featureFilters}
        statusFilters={statusFilters}
        onTeamFiltersChange={onTeamFiltersChange}
        onFeatureFiltersChange={onFeatureFiltersChange}
        onStatusFiltersChange={onStatusFiltersChange}
        teamColors={teamColors}
      >
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </FilterControls>

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <Card className="w-full overflow-hidden">
          <div className="p-6">
            <FeatureTimeline
              deliverables={filteredResults}
              teamColors={teamColors}
              groupMode={groupMode}
              onGroupModeChange={setGroupMode}
              featureOrder={featureOrder}
              featureDueDates={featureDueDates}
              simulationId={simulationId}
              simulationStartDate={simulationStartDate}
              onDeliverableClick={handleDeliverableClick}
              dependencyMap={dependencyMap}
              deliverableLookup={deliverableLookup}
            />
          </div>
        </Card>
      )}

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="space-y-8">
          {filteredTeams.length === 0 ? (
            <div className="py-12 text-center text-text-muted">No deliverables match the current filters</div>
          ) : (
            filteredTeams.map(([teamName, deliverables]) => {
              const teamColor = teamColors[teamName] ?? FALLBACK_COLOR;
              return (
                <section key={teamName} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="size-3 rounded-full" style={{ backgroundColor: teamColor }} />
                    <h2 className="text-xl font-semibold text-text-primary">{teamName}</h2>
                    <span className="text-sm text-text-muted">
                      ({deliverables.length} deliverable{deliverables.length !== 1 ? 's' : ''})
                    </span>
                  </div>

                  <div className="space-y-4">
                    {deliverables.map((deliverable, index) => (
                      <FeatureResultCard
                        key={`${deliverable.team_name}-${deliverable.feature_name ?? 'no-feature'}-${deliverable.deliverable_name}`}
                        result={deliverable}
                        teamColor={teamColor}
                        animationDelay={index * 100}
                        simulationStartDate={simulationStartDate}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
